import React, { useEffect, useState, useCallback } from "react";
import { Plus, Search, Trash2, MapPin, Mail, Phone, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", country: "", contact_email: "", contact_phone: "", tags: "", notes: "" });

  const load = useCallback(async (query = "") => {
    const r = await api.get("/admin/suppliers", { params: query ? { q: query } : {} });
    setSuppliers(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const search = (e) => { setQ(e.target.value); load(e.target.value); };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/suppliers", {
        ...form,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      });
      toast.success("Supplier added");
      setForm({ name: "", category: "", country: "", contact_email: "", contact_phone: "", tags: "", notes: "" });
      setShowForm(false);
      load(q);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/suppliers/${id}`);
    toast.success("Supplier removed");
    load(q);
  };

  const inp = "w-full bg-white border border-zinc-300 rounded-sm px-4 py-2.5 text-sm focus:border-coral focus:ring-1 focus:ring-coral outline-none transition";

  return (
    <div className="max-w-5xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-coral text-xs font-bold uppercase tracking-widest mb-2">Admin Portal</p>
          <h1 className="font-serif text-4xl font-bold tracking-tight">Supplier Directory</h1>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="bg-coral hover:bg-coral-hover text-white font-semibold px-5 py-3 rounded-sm transition flex items-center gap-2" data-testid="add-supplier-btn">
          {showForm ? <X size={18} /> : <Plus size={18} />} {showForm ? "Cancel" : "Add Supplier"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-white border border-zinc-200 rounded-sm p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid="supplier-form">
          <input required placeholder="Supplier name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="supplier-name" />
          <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp} data-testid="supplier-category" />
          <input placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inp} data-testid="supplier-country" />
          <input type="email" placeholder="Contact email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inp} data-testid="supplier-email" />
          <input placeholder="Contact phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inp} data-testid="supplier-phone" />
          <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inp} data-testid="supplier-tags" />
          <textarea placeholder="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inp} sm:col-span-2`} data-testid="supplier-notes" />
          <button className="sm:col-span-2 bg-ink hover:bg-zinc-800 text-white font-semibold py-3 rounded-sm transition" data-testid="save-supplier">Save Supplier</button>
        </form>
      )}

      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
        <input value={q} onChange={search} placeholder="Search by name, category, country, tag..." className="w-full bg-white border border-zinc-300 rounded-sm pl-11 pr-4 py-3 text-sm focus:border-coral focus:ring-1 focus:ring-coral outline-none" data-testid="supplier-search" />
      </div>

      {suppliers.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-16 text-center text-zinc-500" data-testid="empty-suppliers">
          No suppliers found. Add your first vendor.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {suppliers.map((s) => (
            <div key={s.id} className="bg-white border border-zinc-200 rounded-sm p-5 group" data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-serif text-lg font-bold">{s.name}</h3>
                  {s.category && <span className="text-xs font-semibold text-coral uppercase tracking-wide">{s.category}</span>}
                </div>
                <button onClick={() => remove(s.id)} className="text-zinc-300 hover:text-coral transition" data-testid={`delete-supplier-${s.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-zinc-600">
                {s.country && <div className="flex items-center gap-2"><MapPin size={14} /> {s.country}</div>}
                {s.contact_email && <div className="flex items-center gap-2"><Mail size={14} /> {s.contact_email}</div>}
                {s.contact_phone && <div className="flex items-center gap-2"><Phone size={14} /> {s.contact_phone}</div>}
              </div>
              {s.tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.tags.map((t) => <span key={t} className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{t}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
